import { Component } from "@angular/core";
import { CommonModule } from "@angular/common";

@Component({
    selector: "vsf-privacy-policy",
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="max-w-4xl mx-auto px-4 py-8">
            <h1 class="text-3xl font-bold mb-6">Privacy Policy</h1>
            <div class="prose max-w-none space-y-6 text-gray-700">
                <h2 class="text-2xl font-semibold text-gray-900">
                    Privacy Policy for Venduro, UAB
                </h2>

                <p>
                    Protecting the personal data of individuals is a fundamental
                    right. Everyone deserves the right to privacy concerning
                    their personal information. At Venduro, UAB, we recognize
                    and respect this right. The General Data Protection
                    Regulation (GDPR) serves as the framework for legally
                    processing personal data.
                </p>

                <p>
                    We've developed this Privacy Policy to govern our handling
                    of personal data. In this policy, we outline the nature of
                    personal data, the types we collect, how we process it, and
                    the rights individuals have in connection with their data.
                </p>

                <p>
                    The aim of our Privacy Policy is to instill confidence in
                    individuals when sharing their personal information with us.
                    We are committed to responsibly managing and safeguarding
                    your personal data.
                </p>

                <h3 class="text-xl font-semibold text-gray-900">
                    Processing of Personal Data
                </h3>

                <h4 class="text-lg font-medium text-gray-900">
                    What is Personal Data?
                </h4>
                <p>
                    Personal data refers to information in any form that
                    identifies or can identify an individual. This includes
                    names, contact details, order information, product details,
                    and more.
                </p>

                <h4 class="text-lg font-medium text-gray-900">
                    General Principles for Processing Personal Data within
                    Venduro:
                </h4>
                <ul class="list-disc pl-6 space-y-2">
                    <li>
                        We collect personal data only for specific, legitimate
                        purposes.
                    </li>
                    <li>
                        We gather only the necessary personal data to fulfill
                        those purposes.
                    </li>
                    <li>
                        Personal data is used solely for the intended purpose,
                        as outlined in this Privacy Policy. Any alternative use
                        will require your consent.
                    </li>
                    <li>
                        We do not share personal data with third parties unless
                        stated otherwise in this policy, and we seek your
                        consent for any additional sharing.
                    </li>
                    <li>We strive to maintain accurate and up-to-date data.</li>
                    <li>
                        We implement technical and organizational measures to
                        protect personal data from unauthorized access,
                        disclosure, or alteration.
                    </li>
                    <li>
                        Personal data is retained only for as long as necessary
                        to fulfill its purpose or as required by law.
                    </li>
                </ul>

                <h3 class="text-xl font-semibold text-gray-900">
                    Collection and Processing of Personal Data
                </h3>
                <p>
                    We collect and process personal data through various
                    channels, including our websites, resellers, suppliers, and
                    customer support. The types of personal data we collect and
                    process include:
                </p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Basic identification and contact information</li>
                    <li>Purchasing information</li>
                    <li>Customer service and technical support inquiries</li>
                    <li>Feedback on products and services</li>
                    <li>
                        Behavioral information collected through cookies and
                        similar technologies
                    </li>
                </ul>

                <h3 class="text-xl font-semibold text-gray-900">
                    Use of Personal Data
                </h3>
                <p>
                    We collect and process personal data for legitimate
                    purposes, which include:
                </p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Providing customized marketing content</li>
                    <li>Processing purchase orders and transactions</li>
                    <li>
                        Providing software updates and maintaining delivery
                        information
                    </li>
                    <li>Offering customer service and product warranties</li>
                    <li>Personalizing customer experiences</li>
                    <li>Gathering feedback through surveys and evaluations</li>
                </ul>

                <h3 class="text-xl font-semibold text-gray-900">
                    Storage and Processing of Personal Data
                </h3>
                <p>
                    We store and process personal data within the European
                    Economic Area (EEA). However, data may be transferred to
                    countries outside the EEA for specified purposes, with
                    appropriate legal and security measures in place.
                </p>

                <h3 class="text-xl font-semibold text-gray-900">
                    Disclosure of Personal Data to Third Parties
                </h3>
                <p>
                    We do not sell personal data. However, we may disclose it to
                    third parties under certain circumstances, including legal
                    requirements, service providers, business transfers, or with
                    your consent.
                </p>

                <h3 class="text-xl font-semibold text-gray-900">Your Rights</h3>
                <p>
                    You have rights regarding your personal data, including
                    access, updating, deletion, revoking consent, and objecting
                    to processing. You can exercise these rights by contacting
                    us at support&#64;venduro.io.
                </p>

                <h3 class="text-xl font-semibold text-gray-900">Cookies</h3>
                <p>
                    We use cookies and similar technologies to understand
                    website usage and customize marketing efforts. You can
                    manage cookie settings through your browser preferences.
                </p>

                <p>We use the following types of cookies on our website:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>
                        <strong>Essential (strictly necessary) cookies:</strong>
                        Indispensable for maintaining the fundamental
                        functionality of our website, so they are set to users'
                        devices without users' explicit consent. Essential
                        cookies are necessary to provide our services and ensure
                        our websites' features (including the ability to
                        remember your cookie consent preferences) and security.
                    </li>
                    <li>
                        <strong>Functionality cookies:</strong> Allow us to
                        provide a better user experience by remembering
                        information you have entered or choices you have made on
                        our website. For example, this type of cookie ensures
                        that the information provided during your next visit to
                        the website matches your user's preferences.
                    </li>
                    <li>
                        <strong>Analytics cookies:</strong> Collect information
                        about how you use our website and allow us to analyze
                        and improve the performance of our website, as well as
                        help us gain a better understanding of how you navigate
                        through and interact with our website and services.
                    </li>
                    <li>
                        <strong>Advertising cookies:</strong> Record your visits
                        to our website, other pages you have visited, and the
                        links you have clicked. These cookies are placed by us
                        or third-party advertising platforms or networks to
                        provide you with the most appropriate advertising
                        relevant to your interests.
                    </li>
                </ul>

                <p>
                    Our website may also display or redirect content from
                    third-party websites or services, which may result in the
                    storage of cookies from third parties on your hard drive or
                    browser. We do not control the placement and use of cookies
                    by these third parties, and you should review the privacy
                    policies of the relevant third party for more information.
                </p>

                <h4 class="text-lg font-medium text-gray-900">
                    User Interactions
                </h4>
                <p>
                    We partner with Microsoft Clarity and Microsoft Advertising
                    to capture how you use and interact with our website through
                    behavioral metrics, heatmaps, and session replay to improve
                    and market our products/services. Website usage data is
                    captured using first and third-party cookies and other
                    tracking technologies to determine the popularity of
                    products/services and online activity. Additionally, we use
                    this information for site optimization, fraud/security
                    purposes. For more information about how Microsoft collects
                    and uses your data, visit the Microsoft Privacy Statement.
                </p>

                <h3 class="text-xl font-semibold text-gray-900">
                    Data Security
                </h3>
                <p>
                    We employ organizational, technical, and administrative
                    measures to safeguard personal data. We ensure encryption
                    and access controls to protect data integrity.
                </p>

                <h3 class="text-xl font-semibold text-gray-900">
                    Changes to Privacy Policy
                </h3>
                <p>
                    This Privacy Policy may be updated periodically. Significant
                    changes will be communicated to you through announcements or
                    notices.
                </p>

                <h3 class="text-xl font-semibold text-gray-900">Contact Us</h3>
                <p>
                    If you have any questions about this Privacy Policy or our
                    data processing practices, please email us at
                    <a
                        href="mailto:support&#64;venduro.io"
                        class="text-primary-600 hover:text-primary-800"
                        >support&#64;venduro.io</a
                    >.
                </p>
            </div>
        </div>
    `,
})
export class PrivacyPolicyComponent {}
